import {
  OktonLoadingCard,
  OktonErrorCard,
  OktonPageHeader,
  OktonSearchFilter,
  OktonDataGrid,
  OktonEmptyState,
  OktonConfirmDialog,
  ColunaGrid,
  useGrid,
  TAMANHOS_PAGINA,
} from '@okton/platform-ui';
import {
  Box,
  Button,
  Flex,
  IconButton,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import BankAppPage from '../../layout/BankAppPage';
import {
  Agencia,
  useAgenciasCadastro,
  useDeleteAgencia,
} from '../../services/hooks/useAgencias';
import { identificacaoAgencia } from '../../services/hooks/useContasBancarias';
import { colunasDataGrid } from '../../utils/colunasDataGrid';

const COLUNAS: ColunaGrid<Agencia>[] = [
  {
    chave: 'id_agc',
    titulo: 'Código',
    valor: a => Number(a.id_agc),
    tipo: 'numero',
    largura: '90px',
  },
  {
    chave: 'agencia_agc',
    titulo: 'Agência',
    // Ordena pelo número, não pelo texto com sufixo: 1234-6 e 1234-1 ficam
    // juntas, que é como quem procura espera achar.
    valor: a => a.agencia_agc,
  },
  { chave: 'nome_ban', titulo: 'Banco', valor: a => a.nome_ban },
  { chave: 'descricao_agc', titulo: 'Descrição', valor: a => a.descricao_agc },
  { chave: 'gerente_agc', titulo: 'Gerente', valor: a => a.gerente_agc },
  { chave: 'telefone_agc', titulo: 'Telefone', valor: a => a.telefone_agc },
];

/**
 * Cadastro das Agências Bancárias.
 *
 * A Agência é do IFLEX: esta tela é outra porta para o mesmo cadastro, e por
 * isso exige também a permissão do módulo Agências. Excluir recusa a que
 * alguma Conta ainda usa — a tabela não tem chave estrangeira, e apagar
 * deixaria a Conta apontando para o vazio.
 */
const Agencias = () => {
  const history = useHistory();
  const bg = useColorModeValue('white', 'gray.800');

  const {
    data: agencias = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useAgenciasCadastro();
  const deleteMutation = useDeleteAgencia();
  const toast = useToast();
  const [excluindo, setExcluindo] = useState<string | null>(null);
  const [itemExcluir, setItemExcluir] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const {
    ordenacao,
    alternarOrdenacao,
    itens: visiveis,
    filtrados,
    pagina,
    setPagina,
    tamanhoPagina,
    setTamanhoPagina,
  } = useGrid(agencias, COLUNAS, busca);

  const excluir = async () => {
    if (!itemExcluir) return;
    setExcluindo(itemExcluir);
    try {
      await deleteMutation.mutateAsync(itemExcluir);
      setItemExcluir(null);
    } catch (err) {
      toast({
        title:
          err instanceof Error ? err.message : 'Erro ao excluir a Agência.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    } finally {
      setExcluindo(null);
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Agências"
        subtitle="Agências bancárias usadas pelas Contas"
        actions={
          <>
            <Flex ml="auto" my="3" align="center" gridGap={2} flexWrap="wrap">
              <OktonSearchFilter
                value={busca}
                onChange={setBusca}
                placeholder="Buscar por agência, banco, gerente..."
              />
              <Flex
                borderWidth="1px"
                p="2"
                rounded="2xl"
                bg={bg}
                align="center"
                gridGap={2}
              >
                <Button
                  leftIcon={<Plus size={18} />}
                  colorScheme="blue"
                  onClick={() => history.push('/agencias/nova')}
                  minW="150px"
                >
                  Nova Agência
                </Button>
              </Flex>
            </Flex>
          </>
        }
      />

      {isLoading ? (
        <Flex mt="1">
          <OktonLoadingCard h="200px" />
        </Flex>
      ) : isError ? (
        <Flex mt="1">
          <OktonErrorCard
            h="200px"
            description={
              error instanceof Error && error.message
                ? error.message
                : 'Tente novamente. Se o problema persistir, contate o suporte.'
            }
            onRetry={refetch}
          />
        </Flex>
      ) : (
        <Box bg={bg} mt="1" rounded="2xl">
          {filtrados.length === 0 ? (
            <OktonEmptyState
              icon={MapPin}
              title={
                busca
                  ? 'Nada encontrado para a busca'
                  : 'Nenhuma agência cadastrada'
              }
            />
          ) : (
            <OktonDataGrid
              rows={visiveis}
              columns={[
                ...colunasDataGrid(COLUNAS, ordenacao, alternarOrdenacao, {
                  agencia_agc: a => (
                    <Box fontWeight="bold">
                      {identificacaoAgencia(
                        a.agencia_agc,
                        a.digito_agc,
                        a.posto_agc,
                      )}
                    </Box>
                  ),
                }),
                {
                  key: 'acoes',
                  header: null,
                  align: 'right',
                  render: a => (
                    <Flex justify="flex-end" gridGap="1">
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        leftIcon={<Pencil size={14} />}
                        onClick={() => history.push(`/agencias/${a.id_agc}`)}
                      >
                        Editar
                      </Button>
                      <IconButton
                        aria-label="Excluir"
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        icon={<Trash2 size={16} />}
                        isLoading={excluindo === a.id_agc}
                        onClick={() => setItemExcluir(a.id_agc)}
                      />
                    </Flex>
                  ),
                },
              ]}
              getRowId={a => a.id_agc}
              page={pagina + 1}
              pageSize={tamanhoPagina}
              total={filtrados.length}
              pageSizeOptions={TAMANHOS_PAGINA}
              onPageChange={nova => setPagina(nova - 1)}
              onPageSizeChange={setTamanhoPagina}
            />
          )}
        </Box>
      )}
      <OktonConfirmDialog
        isOpen={Boolean(itemExcluir)}
        onClose={() => {
          if (!excluindo) setItemExcluir(null);
        }}
        onConfirm={excluir}
        title="Confirmar exclusão"
        description="Excluir esta Agência do IFLEX?"
        confirmLabel="Excluir"
        intent="danger"
        isLoading={Boolean(excluindo)}
      />
    </BankAppPage>
  );
};

export default Agencias;
